import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { featuredProducts, categories } from "@/mocks/products";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

const allProducts = [
  ...featuredProducts,
  { id: 9, name: "Industrial Air Compressor 50L", price: 320, minOrder: 5, unit: "units", vendor: "Zhejiang Compressor Co.", country: "China", verified: true, rating: 4.5, reviews: 312, image: "https://public.readdy.ai/ai/img_res/f546baaf25a20c1fbb0fe1d04c1b1c6e.jpg", badge: "", category: "Machinery" },
  { id: 10, name: "Bamboo Cutting Board Set 3pcs", price: 6.5, minOrder: 200, unit: "sets", vendor: "Anji Bamboo Products", country: "China", verified: false, rating: 4.4, reviews: 890, image: "https://readdy.ai/api/search-image?query=bamboo%20cutting%20board%20set%203%20pieces%20kitchen%20on%20clean%20white%20background%20professional%20product%20photography%20eco%20friendly&width=400&height=300&seq=mp2&orientation=landscape", badge: "Eco", category: "Home & Garden" },
  { id: 11, name: "Hydraulic Brake Pads OEM", price: 12, minOrder: 100, unit: "sets", vendor: "Mumbai Auto Components", country: "India", verified: true, rating: 4.7, reviews: 1240, image: "https://readdy.ai/api/search-image?query=hydraulic%20brake%20pads%20automotive%20parts%20on%20clean%20white%20background%20professional%20product%20photography%20car%20spare%20parts&width=400&height=300&seq=mp3&orientation=landscape", badge: "", category: "Auto Parts" },
  { id: 12, name: "Collagen Face Cream 50ml OEM", price: 3.8, minOrder: 500, unit: "pcs", vendor: "Guangzhou Beauty OEM", country: "China", verified: true, rating: 4.6, reviews: 2100, image: "https://readdy.ai/api/search-image?query=collagen%20face%20cream%2050ml%20jar%20skincare%20on%20clean%20white%20background%20professional%20product%20photography%20beauty%20cosmetics&width=400&height=300&seq=mp4&orientation=landscape", badge: "Hot", category: "Health & Beauty" },
];

const badgeColors: Record<string, string> = {
  "Top Seller": "bg-amber-400 text-white",
  "Hot": "bg-red-500 text-white",
  "Eco": "bg-green-500 text-white",
  "New": "bg-blue-500 text-white",
};

export default function MarketplacePage() {
  useSEO({
    title: "Global Marketplace | Factory Direct Products | Crown Global",
    description: "Browse millions of factory-direct products from verified global suppliers. Filter by category, price, and rating. Bulk orders, wholesale pricing, and container-load sourcing on Crown Global Marketplace.",
    keywords: "global marketplace, factory direct products, wholesale suppliers, bulk orders, verified manufacturers",
    canonical: "/marketplace",
    ogTitle: "Global Marketplace — Crown Global Factory & Buyer",
    ogDescription: "Discover millions of products from verified suppliers worldwide. Bulk orders, wholesale pricing, factory direct.",
    ogImage: "https://readdy.ai/api/search-image?query=global%20marketplace%20factory%20products%20wholesale%20trade%20professional%20commercial%20photography%20vibrant%20colorful%20products%20display&width=1200&height=630&seq=og_marketplace_01&orientation=landscape",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/marketplace`,
          "url": `${SITE_URL}/marketplace`,
          "name": "Global Marketplace — Crown Global Factory & Buyer",
          "description": "Browse millions of factory-direct products from verified global suppliers on Crown Global Marketplace.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Marketplace", "item": `${SITE_URL}/marketplace` }
            ]
          }
        },
        {
          "@type": "ItemList",
          "name": "Factory Direct Products",
          "description": "Curated list of factory-direct wholesale products from verified global suppliers",
          "url": `${SITE_URL}/marketplace`,
          "numberOfItems": allProducts.length,
          "itemListElement": allProducts.slice(0, 6).map((p, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Product",
              "name": p.name,
              "description": `Factory direct ${p.name} from ${p.vendor}. Min order: ${p.minOrder} ${p.unit}.`,
              "offers": {
                "@type": "Offer",
                "price": p.price,
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "seller": { "@type": "Organization", "name": p.vendor }
              }
            }
          }))
        }
      ]
    }
  });

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedCat, setSelectedCat] = useState("All");
  const [sortBy, setSortBy] = useState("featured");
  const [priceRange, setPriceRange] = useState("all");

  const filtered = allProducts.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.vendor.toLowerCase().includes(search.toLowerCase());
    const matchCat = selectedCat === "All" || p.category === selectedCat;
    const matchPrice =
      priceRange === "all" ? true :
      priceRange === "under10" ? p.price < 10 :
      priceRange === "10to100" ? p.price >= 10 && p.price <= 100 :
      p.price > 100;
    return matchSearch && matchCat && matchPrice;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "price_asc") return a.price - b.price;
    if (sortBy === "price_desc") return b.price - a.price;
    if (sortBy === "rating") return b.rating - a.rating;
    return 0;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Global Marketplace</h1>
            <p className="text-gray-500 text-sm">Discover millions of products from verified suppliers worldwide</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="w-full lg:w-64 shrink-0">
              <div className="bg-white rounded-xl p-5 mb-4">
                <h3 className="font-bold text-gray-800 text-sm mb-3">Categories</h3>
                <div className="flex flex-col gap-1">
                  {["All", ...categories.map((c) => c.name)].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCat(cat)}
                      className={`text-left text-sm px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${selectedCat === cat ? "bg-amber-400 text-white font-semibold" : "text-gray-600 hover:bg-amber-50 hover:text-amber-600"}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5">
                <h3 className="font-bold text-gray-800 text-sm mb-3">Price Range</h3>
                <div className="flex flex-col gap-1">
                  {[
                    { label: "All Prices", value: "all" },
                    { label: "Under $10", value: "under10" },
                    { label: "$10 – $100", value: "10to100" },
                    { label: "Above $100", value: "above100" },
                  ].map((r) => (
                    <button
                      key={r.value}
                      onClick={() => setPriceRange(r.value)}
                      className={`text-left text-sm px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${priceRange === r.value ? "bg-amber-400 text-white font-semibold" : "text-gray-600 hover:bg-amber-50 hover:text-amber-600"}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Main */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="flex flex-1 rounded-full overflow-hidden border border-gray-200 bg-white">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search products or suppliers..."
                    className="flex-1 px-4 py-2.5 text-sm text-gray-700 outline-none"
                  />
                  <button className="bg-amber-400 px-5 flex items-center justify-center cursor-pointer">
                    <i className="ri-search-line text-white"></i>
                  </button>
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-200 bg-white rounded-full px-4 py-2.5 text-sm text-gray-600 outline-none cursor-pointer"
                >
                  <option value="featured">Featured</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="rating">Top Rated</option>
                </select>
              </div>

              <p className="text-xs text-gray-400 mb-4">{sorted.length} products found</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-product-shop>
                {sorted.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => navigate("/product-listings")}
                    className="bg-white rounded-xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      {product.badge && (
                        <span className={`absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full ${badgeColors[product.badge] || ""}`}>{product.badge}</span>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-gray-400 mb-1">{product.category}</p>
                      <h3 className="text-sm font-semibold text-gray-800 leading-snug mb-2 line-clamp-2">{product.name}</h3>
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-amber-500 font-extrabold text-base">${product.price.toLocaleString()}</span>
                        <span className="text-gray-400 text-xs">/ {product.unit}</span>
                      </div>
                      <p className="text-xs text-gray-400 mb-3">Min. {product.minOrder} {product.unit}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 truncate">{product.vendor}</span>
                        {product.verified && <span className="text-green-600 flex items-center gap-0.5 shrink-0 ml-1"><i className="ri-shield-check-fill"></i> Verified</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
